import { supabase } from '@/shared/services/supabaseClient';
import { useAuthStore } from '@/shared/stores/authStore';
import type { Database } from '@/types/database.types';

type Tables = Database['public']['Tables'];

/** Union of all public table names. */
export type TableName = keyof Tables;

/** Row type (SELECT result) for a given table. */
export type Row<N extends TableName> = Tables[N]['Row'];

/** Insert type for a given table. */
type Insert<N extends TableName> = Tables[N]['Insert'];

/** Update type for a given table. */
type Update<N extends TableName> = Tables[N]['Update'];

/** Fields auto-injected by BaseService — callers never provide these. */
type AutoFields =
  | 'id'
  | 'organization_id'
  | 'created_by'
  | 'updated_by'
  | 'created_at'
  | 'updated_at'
  | 'deleted_at'
  | 'metadata';

/*
 * NOTE ON TYPE ASSERTIONS IN THIS FILE:
 *
 * BaseService uses generic table name parameter N, but Supabase's typed client
 * resolves column names and data shapes via conditional types that TypeScript
 * cannot evaluate for generic (unresolved) N. Column name strings like 'id' and
 * 'organization_id' exist on ALL tables (enforced by CLAUDE.md §6), but TypeScript
 * cannot verify this statically for generic N. We use `as never` casts on column
 * names and query data because `never` (the bottom type) satisfies any expected
 * type without introducing `any`. Type safety is maintained by:
 *   1. N is constrained to valid table names (TableName)
 *   2. Return types are explicitly Row<N>, resolving to concrete types in subclasses
 *   3. RLS policies enforce data integrity at runtime
 */

/**
 * BaseService — abstract data access layer with multi-tenant enforcement.
 * Every query automatically filters by organization_id and soft-delete status.
 *
 * Generic parameter `N` is the literal table name (e.g. `'processes'`).
 * Row, Insert, and Update types are derived from the Database schema.
 */
export abstract class BaseService<N extends TableName> {
  constructor(protected readonly tableName: N) {}

  /**
   * Get the current user's organization_id from the auth store.
   * Throws if not authenticated or no organization selected.
   */
  protected getOrgId(): string {
    const orgId = useAuthStore.getState().organizationId;
    if (!orgId) {
      throw new Error('No organization selected');
    }
    return orgId;
  }

  /**
   * Get the current user's ID from the auth store.
   */
  protected getUserId(): string {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('Not authenticated');
    }
    return user.id;
  }

  /**
   * Create a query builder scoped to the current organization, excluding soft-deleted records.
   */
  protected query() {
    return supabase
      .from(this.tableName)
      .select('*')
      .eq('organization_id' as never, this.getOrgId() as never)
      .is('deleted_at' as never, null as never);
  }

  /**
   * Get a single record by ID, scoped to the current organization.
   */
  async getById(id: string): Promise<Row<N> | null> {
    const result = await supabase
      .from(this.tableName)
      .select('*')
      .eq('id' as never, id as never)
      .eq('organization_id' as never, this.getOrgId() as never)
      .is('deleted_at' as never, null as never)
      .single();

    if (result.error) {
      if (result.error.code === 'PGRST116') return null; // Not found
      throw result.error;
    }
    return result.data as unknown as Row<N>;
  }

  /**
   * Insert a new record, auto-injecting organization_id and created_by.
   */
  async insert(record: Omit<Insert<N>, AutoFields>): Promise<Row<N>> {
    const userId = this.getUserId();
    const orgId = this.getOrgId();

    const result = await supabase
      .from(this.tableName)
      .insert({
        ...record,
        organization_id: orgId,
        created_by: userId,
        updated_by: userId,
      } as never)
      .select()
      .single();

    if (result.error) throw result.error;
    return result.data as unknown as Row<N>;
  }

  /**
   * Update a record by ID, auto-injecting updated_by.
   */
  async update(id: string, changes: Partial<Omit<Update<N>, AutoFields>>): Promise<Row<N>> {
    const userId = this.getUserId();

    const result = await supabase
      .from(this.tableName)
      .update({
        ...changes,
        updated_by: userId,
      } as never)
      .eq('id' as never, id as never)
      .eq('organization_id' as never, this.getOrgId() as never)
      .is('deleted_at' as never, null as never)
      .select()
      .single();

    if (result.error) throw result.error;
    return result.data as unknown as Row<N>;
  }

  /**
   * Soft-delete a record by setting deleted_at.
   */
  async softDelete(id: string): Promise<void> {
    const userId = this.getUserId();

    const { error } = await supabase
      .from(this.tableName)
      .update({
        deleted_at: new Date().toISOString(),
        updated_by: userId,
      } as never)
      .eq('id' as never, id as never)
      .eq('organization_id' as never, this.getOrgId() as never)
      .is('deleted_at' as never, null as never);

    if (error) throw error;
  }
}
